import classNames from 'classnames';
import type { Delta, Parchment } from 'quill/core';
import Quill from 'quill/core';
import type { MouseEventHandler } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import Menu, { MenuItem } from '../components/Menu';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import caretBold from '../icons/non-prefilled/16x16/caretBold.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useShortcut from '../utils/useShortcut';
import type { GutterButtonProps } from './Gutter';
import GutterButton from './GutterButton';
import * as styles from './ListReorderGutterButton.module.scss';
import type { DraggableBlot } from './ReorderGutterButton';
import ReorderGutterButton, {
  BaseReorderGutterButton,
} from './ReorderGutterButton';
import CollapsibleListItem from './formats/CollapsibleListItem';
import { getListCollapse } from './useListCollapse';
import getItemAllLines from './utils/getItemAllLines';
import isInlineTextInsertion from './utils/isInlineTextInsertion';

const MENU_SCREEN_PADDING = 10;
const MENU_OFFSET = 16;

const ListReorderGutterButton = ({
  quill,
  activeBlot,
  onLockedChange,
  config,
  ...props
}: GutterButtonProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const listCollapseModule = getListCollapse(quill);
  const menuOpenEventPosRef = useRef<{ left: number; top: number } | null>(
    null,
  );

  const [collapsedState, setCollapseState] = useState({
    isCollapsed: false,
    isCollapsible: false,
    blot: null as Parchment.Blot | null,
  });

  useEffect(() => {
    const updateCollapseState = () => {
      setCollapseState({
        blot: activeBlot,
        isCollapsed: listCollapseModule?.isCollapsed(activeBlot) ?? false,
        isCollapsible:
          !!listCollapseModule?.getNestedListItems(activeBlot).length,
      });
    };

    updateCollapseState();

    const handleTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;

      updateCollapseState();
    };

    quill.on(Quill.events.TEXT_CHANGE, handleTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handleTextChange);
    };
  }, [activeBlot, listCollapseModule, quill]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [activeBlot]);

  useEffect(() => {
    listCollapseModule?.setHighlightedListItem(activeBlot);
    return () => listCollapseModule?.setHighlightedListItem(null);
  }, [activeBlot, listCollapseModule]);

  useShortcut(
    'esc',
    () => {
      setIsMenuOpen(false);
    },
    { skip: !isMenuOpen },
  );

  const isLocked = isDragging || isMenuOpen;

  useEffect(() => {
    if (isLocked) {
      onLockedChange(true);
    }
    return () => onLockedChange(false);
  }, [onLockedChange, isLocked]);

  if (
    collapsedState.blot !== activeBlot ||
    (!config.listCollapsingEnabled && !collapsedState.isCollapsed)
  ) {
    return null;
  }

  const handleContextMenu: MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault();
    menuOpenEventPosRef.current = { left: e.clientX, top: e.clientY };
    setIsMenuOpen(true);
  };

  const handleToggleListItem = () => {
    if (activeBlot instanceof CollapsibleListItem && listCollapseModule) {
      const { isCollapsed } = listCollapseModule.toggleCollapse(activeBlot);
      setCollapseState((state) => ({ ...state, isCollapsed }));
    }
    setIsMenuOpen(false);
  };

  const getItemsToDrag = (blot: DraggableBlot) => [
    ...(getItemAllLines(blot) ?? [blot]),
    ...(listCollapseModule?.getNestedListItems(blot) ?? []),
  ];

  const handleClickOutside = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  const revealDropTarget = (
    target: DraggableBlot,
    draggedBlots: DraggableBlot[],
  ) => {
    if (
      !(target instanceof CollapsibleListItem) ||
      !target.isGloballyCollapsed() ||
      !listCollapseModule
    ) {
      return target;
    }
    const droppedItemIndent =
      draggedBlots[0] instanceof CollapsibleListItem
        ? draggedBlots[0].getIndent()
        : 0;

    let newTarget = target;
    let parentElement = target.getIndent() < droppedItemIndent ? target : null;
    while (
      newTarget.next instanceof CollapsibleListItem &&
      newTarget.next.getIndent() > target.getIndent()
    ) {
      newTarget = newTarget.next;
      if (newTarget.getIndent() < droppedItemIndent) {
        parentElement = newTarget;
      }
    }

    if (parentElement) {
      listCollapseModule.revealBlot(parentElement);
      listCollapseModule?.toggleCollapse(parentElement, false);
    }

    return newTarget;
  };

  return (
    <ReorderGutterButton
      quill={quill}
      activeBlot={activeBlot}
      config={config}
      onLockedChange={setIsDragging}
      revealDropTarget={revealDropTarget}
      {...props}
    >
      {({ onDraggingStart }) => (
        <ClickOutsideHandler onClickOutside={handleClickOutside}>
          <PopOverPrimitive
            screenPadding={MENU_SCREEN_PADDING}
            trigger="none"
            open={isMenuOpen}
            placement="bottom-start"
            offset={MENU_OFFSET}
            onOpenChange={setIsMenuOpen}
            noFlip
          >
            <PopOverPrimitive.Trigger asChild>
              <div className={styles.wrapper}>
                {collapsedState.isCollapsible && (
                  <GutterButton
                    onContextMenu={handleContextMenu}
                    className={classNames(styles.collapsingIconButton, {
                      [styles.isCollapsed]: collapsedState.isCollapsed,
                    })}
                    aria-label="Toggle collapse"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleListItem();
                    }}
                  >
                    <SVGIcon size={8} className={styles.collapsingIcon}>
                      {caretBold}
                    </SVGIcon>
                  </GutterButton>
                )}
                <div
                  onContextMenu={handleContextMenu}
                  aria-hidden={!config.reorderingEnabled}
                  className={classNames(styles.reorderGutterButton, {
                    [styles.hidden]: !config.reorderingEnabled,
                  })}
                >
                  <BaseReorderGutterButton
                    quill={quill}
                    activeBlot={activeBlot}
                    onDraggingStart={(blot, position) =>
                      onDraggingStart?.(getItemsToDrag(blot), position)
                    }
                  />
                </div>
              </div>
            </PopOverPrimitive.Trigger>
            <PopOverPrimitive.Floating noPortal>
              <PopOverPrimitive.Content>
                <Menu>
                  <MenuItem
                    onClick={handleToggleListItem}
                    disabled={
                      !collapsedState.isCollapsible &&
                      !collapsedState.isCollapsed
                    }
                  >
                    {collapsedState.isCollapsed ? 'Expand' : 'Collapse'}
                  </MenuItem>
                  {config.reorderingEnabled && (
                    <MenuItem
                      onClick={() => {
                        if (menuOpenEventPosRef.current) {
                          onDraggingStart?.(
                            getItemsToDrag(activeBlot),
                            menuOpenEventPosRef.current,
                          );
                          setIsMenuOpen(false);
                        }
                      }}
                    >
                      Move item
                    </MenuItem>
                  )}
                </Menu>
              </PopOverPrimitive.Content>
            </PopOverPrimitive.Floating>
          </PopOverPrimitive>
        </ClickOutsideHandler>
      )}
    </ReorderGutterButton>
  );
};

export default ListReorderGutterButton;
